import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, UserPlus, Shield, Users } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';

type Permission = {
  id: string;
  user_id: string;
  module: string;
  submodule?: string;
  permission_type: 'read' | 'write' | 'admin';
  created_at: string;
  granted_by?: string;
  profiles?: {
    full_name: string;
    email: string;
  };
};

type Profile = {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
};

type UserRole = {
  id: string;
  user_id: string;
  role: 'admin' | 'user' | 'manager';
  created_at: string;
};

const modules = [
  'administracao',
  'ia',
  'clientes',
  'insumos',
  'departamento_pessoal',
  'marketing',
  'comercial',
  'juridico',
  'financeiro'
];

const submodules = {
  administracao: ['usuarios', 'tipo_usuarios', 'permissoes', 'pedidos_acesso', 'log_sistema'],
  ia: ['ariana'],
  clientes: ['clientes', 'tipo_clientes', 'contatos', 'plantoes'],
  insumos: ['produtos', 'tarifario', 'estoque', 'orcamento', 'pedidos', 'entregas', 'fretes', 'tipo_fretes', 'pagamentos'],
  departamento_pessoal: ['contratacao', 'estagios_contratacao', 'evolucao_contratacao'],
  marketing: ['produtos_servicos', 'campanhas', 'resultados'],
  comercial: ['prospects', 'contatos', 'agendamentos', 'propostas'],
  juridico: ['processos'],
  financeiro: ['contas_pagar', 'contas_receber']
};

export default function Permissoes() {
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedSubmodule, setSelectedSubmodule] = useState<string>('');
  const { toast } = useToast();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const queryClient = useQueryClient();

  // Debug: log para verificar se o componente está carregando
  console.log('🔧 Componente Permissoes carregado', { permissionsLoading, hasPermission: hasPermission('administracao', 'permissoes', 'admin') });

  // Queries - MOVED BEFORE EARLY RETURNS TO FIX HOOKS RULE
  const { data: permissions, isLoading: loadingPermissions } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('permissions')
        .select(`
          *
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  const { data: permissionProfiles } = useQuery({
    queryKey: ['permission_profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, email');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: profiles } = useQuery({
    queryKey: ['profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('full_name');
      if (error) throw error;
      return data as Profile[];
    }
  });

  const { data: userRoles } = useQuery({
    queryKey: ['user_roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select(`*`)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  const { data: accessRequests } = useQuery({
    queryKey: ['access_requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('access_requests')
        .select(`*`)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  // Mutations
  const createPermissionMutation = useMutation({
    mutationFn: async (permission: {
      user_id: string;
      module: string;
      submodule?: string;
      permission_type: 'read' | 'write' | 'admin';
    }) => {
      const { error } = await supabase
        .from('permissions')
        .insert([permission]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      setIsPermissionDialogOpen(false);
      toast({
        title: "Permissão criada",
        description: "A permissão foi criada com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar permissão.",
        variant: "destructive",
      });
    }
  });

  const deletePermissionMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('permissions')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      toast({
        title: "Permissão removida",
        description: "A permissão foi removida com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao remover permissão.",
        variant: "destructive",
      });
    }
  });

  const createRoleMutation = useMutation({
    mutationFn: async (role: {
      user_id: string;
      role: 'admin' | 'user' | 'manager';
    }) => {
      const { error } = await supabase
        .from('user_roles')
        .insert([role]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user_roles'] });
      setIsRoleDialogOpen(false);
      toast({
        title: "Role atribuído",
        description: "O role foi atribuído com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao atribuir role.",
        variant: "destructive",
      });
    }
  });

  const updateAccessRequestMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => {
      const { error } = await supabase
        .from('access_requests')
        .update({ 
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access_requests'] });
      toast({
        title: "Solicitação atualizada",
        description: "A solicitação foi atualizada com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar solicitação.",
        variant: "destructive",
      });
    }
  });

  const handleSubmitPermission = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    createPermissionMutation.mutate({
      user_id: formData.get('user_id') as string,
      module: formData.get('module') as string,
      submodule: formData.get('submodule') as string || undefined,
      permission_type: formData.get('permission_type') as 'read' | 'write' | 'admin',
    });
  };

  const handleSubmitRole = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    createRoleMutation.mutate({
      user_id: formData.get('user_id') as string,
      role: formData.get('role') as 'admin' | 'user' | 'manager',
    });
  };

  const getPermissionBadgeColor = (type: string) => {
    switch (type) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'write': return 'bg-yellow-100 text-yellow-800';
      case 'read': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'user': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Permission checks AFTER all hooks
  if (permissionsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!hasPermission('administracao', 'permissoes', 'admin')) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Acesso Negado</h3>
              <p className="text-muted-foreground">
                Você não tem permissão para gerenciar permissões do sistema.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Debug: hasPermission result = {String(hasPermission('administracao', 'permissoes', 'admin'))}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Gerenciamento de Permissões</h1>
          <p className="text-muted-foreground">
            Gerencie permissões específicas, roles de usuários e solicitações de acesso.
          </p>
        </div>
        <div className="space-x-2">
          <Dialog open={isPermissionDialogOpen} onOpenChange={setIsPermissionDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nova Permissão
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Nova Permissão</DialogTitle>
                <DialogDescription>
                  Adicione uma permissão específica para um usuário em um módulo ou submódulo.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmitPermission}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="user_id">Usuário</Label>
                    <Select name="user_id" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um usuário" />
                      </SelectTrigger>
                      <SelectContent>
                        {profiles?.map((profile) => (
                          <SelectItem key={profile.user_id} value={profile.user_id}>
                            {profile.full_name} ({profile.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="module">Módulo</Label>
                    <Select 
                      name="module" 
                      required 
                      onValueChange={(value) => {
                        setSelectedModule(value);
                        setSelectedSubmodule('');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um módulo" />
                      </SelectTrigger>
                      <SelectContent>
                        {modules.map((module) => (
                          <SelectItem key={module} value={module}>
                            {module.replace('_', ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedModule && submodules[selectedModule as keyof typeof submodules] && (
                    <div>
                      <Label htmlFor="submodule">Submódulo (Opcional)</Label>
                      <Select name="submodule" onValueChange={setSelectedSubmodule}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um submódulo" />
                        </SelectTrigger>
                        <SelectContent>
                          {submodules[selectedModule as keyof typeof submodules].map((submodule) => (
                            <SelectItem key={submodule} value={submodule}>
                              {submodule.replace('_', ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="permission_type">Tipo de Permissão</Label>
                    <Select name="permission_type" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="read">Leitura</SelectItem>
                        <SelectItem value="write">Escrita</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter className="mt-4">
                  <Button type="submit" disabled={createPermissionMutation.isPending}>
                    {createPermissionMutation.isPending ? 'Criando...' : 'Criar Permissão'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <UserPlus className="h-4 w-4 mr-2" />
                Atribuir Role
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Atribuir Role de Usuário</DialogTitle>
                <DialogDescription>
                  Atribua um role global a um usuário (admin, moderator, user).
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmitRole}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="user_id">Usuário</Label>
                    <Select name="user_id" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um usuário" />
                      </SelectTrigger>
                      <SelectContent>
                        {profiles?.map((profile) => (
                          <SelectItem key={profile.user_id} value={profile.user_id}>
                            {profile.full_name} ({profile.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select name="role" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="manager">Gerente</SelectItem>
                        <SelectItem value="user">Usuário</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter className="mt-4">
                  <Button type="submit" disabled={createRoleMutation.isPending}>
                    {createRoleMutation.isPending ? 'Atribuindo...' : 'Atribuir Role'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Permissões Específicas */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Permissões Específicas</CardTitle>
              <CardDescription>
                Permissões detalhadas por módulo e submódulo para cada usuário.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPermissions ? (
                <div className="text-center py-4">Carregando...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead>Módulo</TableHead>
                      <TableHead>Submódulo</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {permissions?.map((permission) => {
                      const profile = permissionProfiles?.find(p => p.user_id === permission.user_id);
                      return (
                      <TableRow key={permission.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{profile?.full_name || 'Usuário não encontrado'}</div>
                            <div className="text-sm text-muted-foreground">{profile?.email || ''}</div>
                          </div>
                        </TableCell>
                        <TableCell>{permission.module.replace('_', ' ')}</TableCell>
                        <TableCell>{permission.submodule?.replace('_', ' ') || '-'}</TableCell>
                        <TableCell>
                          <Badge className={getPermissionBadgeColor(permission.permission_type)}>
                            {permission.permission_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deletePermissionMutation.mutate(permission.id)}
                            disabled={deletePermissionMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Roles dos Usuários */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Roles dos Usuários</CardTitle>
              <CardDescription>
                Roles globais atribuídos aos usuários.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {userRoles?.map((userRole: any) => {
                  const profile = permissionProfiles?.find(p => p.user_id === userRole.user_id);
                  return (
                  <div key={userRole.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <div className="font-medium">{profile?.full_name || 'Usuário não encontrado'}</div>
                      <div className="text-sm text-muted-foreground">{profile?.email || ''}</div>
                    </div>
                    <Badge className={getRoleBadgeColor(userRole.role)}>
                      {userRole.role}
                    </Badge>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Solicitações de Acesso */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Solicitações de Acesso</CardTitle>
              <CardDescription>
                Solicitações pendentes de acesso ao sistema.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {accessRequests?.filter((req: any) => req.status === 'pending').map((request: any) => {
                  const profile = permissionProfiles?.find(p => p.user_id === request.requester_id);
                  return (
                  <div key={request.id} className="p-3 border rounded">
                    <div className="space-y-2">
                      <div>
                        <div className="font-medium">{profile?.full_name || 'Usuário não encontrado'}</div>
                        <div className="text-sm text-muted-foreground">{profile?.email || ''}</div>
                      </div>
                      <div className="text-sm">
                        <strong>Módulo:</strong> {request.module}<br />
                        {request.submodule && <><strong>Submódulo:</strong> {request.submodule}<br /></>}
                        <strong>Tipo:</strong> {request.permission_type}<br />
                        {request.reason && <><strong>Motivo:</strong> {request.reason}</>}
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          onClick={() => updateAccessRequestMutation.mutate({ id: request.id, status: 'approved' })}
                          disabled={updateAccessRequestMutation.isPending}
                        >
                          Aprovar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => updateAccessRequestMutation.mutate({ id: request.id, status: 'rejected' })}
                          disabled={updateAccessRequestMutation.isPending}
                        >
                          Rejeitar
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
                {!accessRequests?.filter((req: any) => req.status === 'pending').length && (
                  <div className="text-center py-4 text-muted-foreground">
                    Nenhuma solicitação pendente
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}